import { useEffect, useState } from "react";
import { FOUR_K_LONG_EDGE } from "../constants";

export type DimensionStatus = "idle" | "loading" | "done" | "error";

export interface ImageDimensions {
  width: number | null;
  height: number | null;
  longEdge: number | null;
  /** Mirrors the BE rule (services/design.py FOUR_K_LONG_EDGE): max(w, h) >= 3840. */
  isAlready4k: boolean;
  status: DimensionStatus;
}

const IDLE: ImageDimensions = { width: null, height: null, longEdge: null, isAlready4k: false, status: "idle" };

// Cache measured dimensions per URL so reopening the menu (or another DownloadButton for the
// same image) is instant and never re-loads. Module-scope: shared across all DownloadButtons.
const dimensionCache = new Map<string, ImageDimensions>();

/**
 * Measures an image's native dimensions client-side. Reading naturalWidth/Height needs no CORS
 * (only canvas pixel reads taint), so GCS-hosted images measure fine. Runs lazily only when
 * `enabled` (menu open + raster), caches the result per URL.
 *
 * Mirrors the BE's "already 4K" rule exactly — max(width, height) >= FOUR_K_LONG_EDGE (3840),
 * aspect-agnostic — and measures the same `imageURL` the BE later measures, so the FE disable
 * and the BE `already_4k` flag agree by construction. On load failure (status "error") it leaves
 * isAlready4k=false → upscale stays enabled, matching the BE, which proceeds to upscale when its
 * own dimension read fails.
 */
export const useImageDimensions = (imageURL: string | undefined, enabled: boolean): ImageDimensions => {
  const [dimensions, setDimensions] = useState<ImageDimensions>(IDLE);

  useEffect(() => {
    if (!enabled || !imageURL) {
      return;
    }

    const cached = dimensionCache.get(imageURL);
    if (cached) {
      setDimensions(cached);

      return;
    }

    let cancelled = false;
    setDimensions({ ...IDLE, status: "loading" });

    const img = new Image();

    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      // A decoded-but-zero-dimension image is effectively a failure — keep upscale enabled.
      if (!width || !height) {
        if (!cancelled) setDimensions({ ...IDLE, status: "error" });

        return;
      }

      const longEdge = Math.max(width, height);
      const result: ImageDimensions = { width, height, longEdge, isAlready4k: longEdge >= FOUR_K_LONG_EDGE, status: "done" };
      dimensionCache.set(imageURL, result);
      if (!cancelled) setDimensions(result);
    };

    img.onerror = () => {
      // Don't cache errors — a transient network failure shouldn't poison future opens.
      if (!cancelled) setDimensions({ ...IDLE, status: "error" });
    };

    img.src = imageURL;

    return () => {
      cancelled = true;
    };
  }, [imageURL, enabled]);

  return dimensions;
};
